import { differenceInDays, parseISO, isValid, startOfDay } from 'date-fns';
import { f7, useStore } from 'framework7-react';
import { FC } from 'react';
import style from './CoatingDetailPageStyle.module.css';
import { Button } from '@/components/uiParts/Button';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import {
  coatingBodySizeOptions,
  coatingPolishOptionOptions,
} from '@/consts/coating';
import { statusReservation } from '@/consts/reservation';
import { useFetchReservationDetail } from '@/hooks/api/reservation/useFetchReservationDetail';
import { Customer } from '@/types/api/customerApi';
import { Reservation } from '@/types/api/reservationApi';
import { formatToJaDate, formatToJaDateTime } from '@/utils/date';

type CoatingDetailPageProps = {
  fromPage?: string;
};

const toJaDate = (value?: string | null): string => {
  if (!value) return '-';
  const d = parseISO(value);
  return isValid(d) ? formatToJaDate(d) : '-';
};

const toJaDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const d = parseISO(value);
  return isValid(d) ? formatToJaDateTime(d) : '-';
};

export const CoatingDetailPage: FC<CoatingDetailPageProps> = ({ fromPage }) => {
  const reservation = useStore(store, 'getReservation') as
    | Reservation
    | undefined;
  const {
    affiliate_name: affiliateName,
    affiliate_invoice_registration_number: invoiceNumber,
  } = useStore(store, 'customerInfo') as Customer;

  const { data } = useFetchReservationDetail({
    reservation_id: reservation?.reservation_id ?? 0,
    service_type: reservation?.service_type ?? 'coating',
  });

  if (!reservation?.reservation_id) {
    return (
      <PageTemplate pageName="CoatingDetail" title="予約内容詳細" showNavBack>
        <div style={{ padding: '32px', textAlign: 'center' }}>
          予約情報が取得できませんでした。
          <br />
          予約一覧からアクセスしてください。
        </div>
      </PageTemplate>
    );
  }

  const status = data?.reservation_status ?? 0;

  // アプリ上での日程変更・キャンセルは「施工実施予定日の3日前まで」受付
  // 施工予定日は確定日があればそれ、なければ第一希望日を採用
  const serviceDateStr =
    data?.confirmed_datetime ?? data?.preferred_datetime?.datetime1 ?? null;
  const serviceDate = serviceDateStr ? parseISO(serviceDateStr) : null;
  const daysUntilService =
    serviceDate && isValid(serviceDate)
      ? differenceInDays(startOfDay(serviceDate), startOfDay(new Date()))
      : null;
  const canSelfModify =
    status !== statusReservation.CANCEL &&
    status !== statusReservation.COMPLETED &&
    daysUntilService !== null &&
    daysUntilService >= 3;

  const bodySizeLabel =
    coatingBodySizeOptions.find((o) => o.value === data?.body_size)?.label ??
    '-';
  const polishLabel =
    coatingPolishOptionOptions.find((o) => o.value === data?.polish_option)
      ?.label ?? '-';

  const contactMethodLabel = data?.contact_method === 2 ? 'メール' : 'お電話';

  const handleClickCancel = () => {
    f7.views.main.router.navigate(
      paths.coatingCancelReason(reservation.reservation_id.toString()),
    );
  };

  const handleClickChangeSchedule = () => {
    f7.views.main.router.navigate(
      paths.coatingEdit(reservation.reservation_id.toString()),
    );
  };

  return (
    <PageTemplate
      pageName="CoatingDetail"
      title="予約内容詳細"
      showNavBack
      handleClickBack={() =>
        f7.view.main.router.navigate(fromPage || paths.reservationHistory, {
          transition: 'push-reverse',
        })
      }
    >
      <div className={style['container']}>
        {/* ステータス */}
        <div className={style['status-box']}>
          <span className={style['service-type']}>ボディコーティング</span>
          <span className={style['status']}>
            {data?.reservation_status_value ?? '-'}
          </span>
        </div>

        {/* 受付情報 */}
        <Section title="受付情報">
          <Row label="予約番号" value={`${reservation.reservation_id}`} />
          <Row label="受付日" value={toJaDate(data?.order_date)} />
        </Section>

        {/* コース・オプション */}
        <Section title="ご予約内容">
          <Row label="コース" value={data?.coating_item?.name ?? '-'} />
          <Row label="車体サイズ" value={bodySizeLabel} />
          <Row label="磨きオプション" value={polishLabel} />
          {!!data?.coating_option?.length && (
            <Row
              label="オプション"
              value={
                <ul className={style['option-list']}>
                  {data.coating_option.map((op, idx) => (
                    <li key={idx}>{op.coating_option_name}</li>
                  ))}
                </ul>
              }
            />
          )}
          {!!data?.body_color && (
            <Row label="ボディカラー" value={data.body_color} />
          )}
          {!!data?.past_coating_history && (
            <Row label="過去コーティング歴" value={data.past_coating_history} />
          )}
        </Section>

        {/* 金額 */}
        <Section title="お支払い金額">
          <Row
            label="合計（税込）"
            value={`¥ ${(data?.total_price ?? 0).toLocaleString()}`}
          />
          {!!data?.cancellation_charge?.charge_percent && (
            <Row
              label="キャンセル料率"
              value={`${data.cancellation_charge.charge_percent}%`}
            />
          )}
        </Section>

        {/* 領収書（決済済み かつ 未キャンセル の場合のみ表示） */}
        {data?.is_paid && status !== statusReservation.CANCEL && (
          <div className={style['section']}>
            <div className={style['receipt-header-border']}>
              <div className={style['receipt-title']}>領収書</div>
            </div>
            <div className={style['receipt-flex']}>
              <span
                className={`${style['receipt-text-gray-normal']} ${style['receipt-affiliate-name']}`}
              >
                {affiliateName}
              </span>
              <span
                className={`${style['receipt-text-gray-normal']} ${style['receipt-invoice-number']}`}
              >
                {invoiceNumber}
              </span>
            </div>
            <p
              className={`${style['receipt-text-gray-normal']} ${style['receipt-order-date-info']}`}
            >
              {toJaDate(data?.paid_at ?? data?.order_date)}
            </p>
            <p className={style['receipt-service-title']}>ボディコーティング</p>
            <div className={style['receipt-box']}>
              <h4 className={style['receipt-box-title']}>お支払額</h4>
              <p
                className={`${style['receipt-text-gray-small']} ${style['receipt-tax']}`}
              >
                (消費税{data?.tax}%込)
              </p>
              <p className={style['receipt-detail']}>
                <span className={style['receipt-amount']}>
                  {(data?.total_price ?? 0).toLocaleString()}
                </span>
                <span className={style['receipt-unit']}>円</span>
              </p>
            </div>
            <p
              className={`${style['receipt-text-gray-small']} ${style['receipt-publication-date-info']}`}
            >
              発行日付：{toJaDate(data?.paid_at)}
            </p>
          </div>
        )}

        {/* 希望日時 */}
        <Section title="ご希望日時">
          <Row
            label="第一希望"
            value={toJaDateTime(data?.preferred_datetime?.datetime1)}
          />
          <Row
            label="第二希望"
            value={toJaDateTime(data?.preferred_datetime?.datetime2)}
          />
          <Row
            label="第三希望"
            value={toJaDateTime(data?.preferred_datetime?.datetime3)}
          />
          {!!data?.confirmed_datetime && (
            <Row
              label="確定日時"
              value={toJaDateTime(data.confirmed_datetime)}
            />
          )}
        </Section>

        {/* 予約者情報 */}
        <Section title="ご予約者情報">
          <Row
            label="お名前"
            value={
              `${data?.reservation_holder?.family_name ?? ''} ${
                data?.reservation_holder?.first_name ?? ''
              }`.trim() || '-'
            }
          />
          <Row
            label="フリガナ"
            value={
              `${data?.reservation_holder?.family_name_kana ?? ''} ${
                data?.reservation_holder?.first_name_kana ?? ''
              }`.trim() || '-'
            }
          />
          <Row
            label="電話番号"
            value={data?.reservation_holder?.phone_no ?? '-'}
          />
          <Row label="ご連絡方法" value={contactMethodLabel} />
          {data?.contact_method === 2 && !!data?.contact_email && (
            <Row label="連絡先メール" value={data.contact_email} />
          )}
        </Section>

        {/* 車両情報 */}
        <Section title="お車の情報">
          <Row
            label="メーカー / 車名"
            value={
              `${data?.car_info?.maker_name ?? ''} ${
                data?.car_info?.car_name ?? ''
              }`.trim() || '-'
            }
          />
          {!!data?.car_info?.registry_no && (
            <Row
              label="ナンバー"
              value={`${data.car_info.transport_name ?? ''} ${
                data.car_info.class_no ?? ''
              } ${data.car_info.hiragana ?? ''} ${
                data.car_info.registry_no ?? ''
              }`.trim()}
            />
          )}
        </Section>

        {/* 備考 */}
        {!!data?.unit_memo && (
          <Section title="備考">
            <p className={style['memo']}>{data.unit_memo}</p>
          </Section>
        )}

        {/* キャンセル情報 */}
        {status === statusReservation.CANCEL && data?.cancellation && (
          <Section title="キャンセル情報">
            {!!data.cancellation.cancellation_datetime && (
              <Row
                label="キャンセル日時"
                value={toJaDateTime(data.cancellation.cancellation_datetime)}
              />
            )}
          </Section>
        )}

        {/* アクションボタン（仮予約・確定済みのいずれでも施工日3日前までは表示） */}
        {canSelfModify && (
          <div className={style['button-group']}>
            <Button text="日程を変更する" onClick={handleClickChangeSchedule} />
            <Button
              style={{
                color: '#232323',
                backgroundColor: '#fff',
                marginTop: '8px',
              }}
              text="キャンセルする"
              onClick={handleClickCancel}
            />
          </div>
        )}
      </div>
    </PageTemplate>
  );
};

type SectionProps = {
  title: string;
  children: React.ReactNode;
};

const Section: FC<SectionProps> = ({ title, children }) => (
  <div className={style['section']}>
    <h3 className={style['section-title']}>{title}</h3>
    <div className={style['section-content']}>{children}</div>
  </div>
);

type RowProps = {
  label: string;
  value: React.ReactNode;
};

const Row: FC<RowProps> = ({ label, value }) => (
  <div className={style['row']}>
    <span className={style['row-label']}>{label}</span>
    <span className={style['row-value']}>{value}</span>
  </div>
);
